<template>
  <article
    v-if="flatPageContent && 'content' in flatPageContent"
    class="promo"
    :class="{ desktop: $isDesktop }"
  >
    <ul class="breadcrumb">
      <li>
        <nuxt-link :to="homePath">
          <translate>Home</translate>
        </nuxt-link>
      </li>
      <li v-for="(breadcrumb, index) in filteredBreadcrumbs" :key="index">
        <nuxt-link :to="breadcrumb.url">
          <span>{{ breadcrumb.title }}</span>
        </nuxt-link>
      </li>
      <li>
        <nuxt-link :to="$route.fullPath">
          <span>{{ title }}</span>
        </nuxt-link>
      </li>
    </ul>
    <div v-html="processedContent?.title" />
    <div v-if="flatPageContent.dateFrom" class="published-date">
      <i18n-t keypath="Published {date}">
        <template #date>
          <pu-date
            :date="flatPageContent.dateFrom"
            :options="{ day: 'numeric', month: 'long', year: 'numeric' }"
          />
        </template>
      </i18n-t>
    </div>
    <div class="flatpagedata" v-html="processedContent?.content" />
  </article>
  <div v-else-if="flatPageContent && 'location' in flatPageContent">
    <p>Redirecting...</p>
  </div>
  <div v-else>
    <p>Loading...</p>
  </div>
</template>

<script setup lang="ts">
import {
  createError,
  navigateTo,
  useAsyncData,
  useHead,
  useNuxtApp,
  useRequestEvent,
  useRoute,
} from 'nuxt/app'
import { computed } from 'vue'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'
import { logger } from '../../utility/logger'
import { FlatPagesRepositoryFetch } from './FlatPagesRepositoryFetch'
import type { FlatPage } from './schema'
const route = useRoute()
const { $config } = useNuxtApp()

definePageMeta({
  validate: (route) => {
    logger('FlatPageComponent.validate', route.path)
    return (
      !route.path.includes('.') &&
      !route.path.startsWith('_') &&
      !route.path.startsWith('/_')
      // Removed trailing slash requirement - let middleware handle it
    )
  },
})

const { data: flatPageContent } = await useAsyncData<
  FlatPage | { location: string }
>(
  `flat-page-${route.path}`,
  async () => {
    // for client if ssr generated error, skip fetching and show error
    if (process.client) {
      // bit of a hack but it works
      const isErrorPage = document.querySelector('.error-wrapper') !== null
      if (isErrorPage) {
        return ''
      }
    }
    const content = await new FlatPagesRepositoryFetch().get(
      route.path,
      (route.params.lang as string) || 'en-gb',
    )
    logger('FlatPageComponent.useAsyncData', 'content', content)
    return content
  },
  {
    transform: (content) => {
      if (content && 'content' in content) {
        content.content = content.content.replace(
          'loading="lazy"',
          'fetchpriority="high" decoding="sync"',
        )
      }
      return content
    },
    server: true,
    lazy: false,
  },
)

// Set the xkey header on the server side
if (process.server) {
  const event = useRequestEvent()
  event.node.res.setHeader('xkey', `view:flatpage flatpage:${route.path}`)
  event.node.res.setHeader(
    'Cache-Control',
    `max-age=${$config.nonPriceCacheTime}, public`,
  )
  event.node.res.setHeader('Grace', `${$config.nonPriceGraceTime}`)
}

// Handle redirect or content
if (flatPageContent.value) {
  if ('location' in flatPageContent.value) {
    await navigateTo(flatPageContent.value.location, {
      redirectCode: 301,
      external: true,
    })
  }
} else {
  throw createError({
    statusCode: 404,
    statusMessage: `Page Not Found: ${route.path}`,
    fatal: process.client, // Only set fatal true on client-side
  })
}

// Compute the home path with i18n support
const homePath = computed(() => {
  const prefix = route.params.lang ? '/' + route.params.lang : ''
  return prefix + '/'
})

// Filter out any server breadcrumbs that match the home URL
// Server can send breadcrumbs with or without language prefix, so we filter out both '/' and '/{lang}/'
const filteredBreadcrumbs = computed(() => {
  if (!flatPageContent.value || !('breadcrumbs' in flatPageContent.value)) {
    return []
  }
  return (
    flatPageContent.value.breadcrumbs?.filter(
      (breadcrumb) =>
        breadcrumb.url !== '/' && breadcrumb.url !== homePath.value,
    ) || []
  )
})

const processedContent = computed(() => {
  if (!flatPageContent.value) {
    return null
  }

  let content = flatPageContent.value.content
  let title = ''

  if (process.server) {
    const titleStart = content.indexOf('<h1')
    const titleEnd = content.indexOf('</h1>') + 5
    if (titleStart !== -1) {
      title = content.slice(titleStart, titleEnd)
      content = content.slice(0, titleStart) + content.slice(titleEnd)
    }
  } else if (process.client) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(content, 'text/html')
    const titleElem = doc.querySelector('h1:first-of-type')
    if (titleElem) {
      title = titleElem.outerHTML
      titleElem.remove()
    }
    content = doc.documentElement.outerHTML
  }

  return {
    content,
    title,
  }
})

const title = computed(() => flatPageContent.value?.title || '')
const description = computed(() => flatPageContent.value?.description || '')
const firstPhoto = computed(() => flatPageContent.value?.firstPhoto || null)
const firstPhotoDescription = computed(
  () => flatPageContent.value?.firstPhotoDescription || '',
)

const articleAbsolutePath = computed(
  () => `https://${$config.public.envUrl}${route.path}`,
)

const ogImageMetaData = computed(() =>
  firstPhoto.value
    ? {
        hid: 'og:image',
        property: 'og:image',
        content: `https://${$config.public.envUrl}${firstPhoto.value}`,
      }
    : {},
)

const ogImageAltMetaData = computed(() =>
  firstPhotoDescription.value
    ? {
        hid: 'og:image:alt',
        property: 'og:image:alt',
        content: firstPhotoDescription.value,
      }
    : {},
)

const schemaOrganization = {
  type: 'application/ld+json',
  body: true,
  innerHTML: JSON.stringify(SCHEMA_ORGANIZATION),
  hid: 'schema-organization',
}

const newsArticleScript = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const json: any = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleAbsolutePath.value,
    },
    headline: title.value,
    description: description.value,
    author: {
      '@type': 'Organization',
      name: 'Pitchup.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pitchup.com',
      alternateName: 'Pitchup',
      logo: {
        '@type': 'ImageObject',
        url: `https://${$config.public.envUrl}/app-icons/pitchup-icon-1024x1024.png`,
      },
    },
  }
  if (flatPageContent.value?.dateFrom) {
    json.datePublished = flatPageContent.value.dateFrom
  }
  if (firstPhoto.value) {
    json.image = firstPhoto.value
  }
  return {
    type: 'application/ld+json',
    body: true,
    innerHTML: JSON.stringify(json),
  }
})

const breadcrumbList = computed(() => {
  const prefix = route.params.lang ? '/' + route.params.lang : ''
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `https://${$config.public.envUrl}${prefix}/`,
          name: 'Home',
        },
      },
      ...(filteredBreadcrumbs.value?.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        item: {
          '@id': `https://${$config.public.envUrl}${prefix}${breadcrumb.url}`,
          name: breadcrumb.title,
        },
      })) || []),
      {
        '@type': 'ListItem',
        position: (filteredBreadcrumbs.value?.length || 0) + 2,
        item: {
          '@id': articleAbsolutePath.value,
          name: title.value,
        },
      },
    ],
  }
  return {
    type: 'application/ld+json',
    body: true,
    innerHTML: JSON.stringify(json),
  }
})

const descriptionMetaData = computed(() => ({
  hid: 'description',
  name: 'description',
  property: 'og:description',
  content: description.value,
}))

const titleMetaData = computed(() => ({
  hid: 'og:title',
  property: 'og:title',
  content: title.value,
}))

const ogUrlMetaData = computed(() => ({
  hid: 'og:url',
  property: 'og:url',
  content: `https://${$config.public.envUrl}${route.path}`,
}))

useHead(() => ({
  title: `${title.value} | Pitchup.com`,
  meta: [
    titleMetaData.value,
    descriptionMetaData.value,
    ogImageMetaData.value,
    ogImageAltMetaData.value,
    ogUrlMetaData.value,
  ],
  bodyAttrs: {
    class: 'promo-page',
  },
  script: [newsArticleScript.value, breadcrumbList.value, schemaOrganization],
}))
</script>

<style scoped>
.promo {
  padding: 1em 16px 7em;
  width: 100%;
}

.breadcrumb {
  margin: 0 0 1em 0;
}

.breadcrumb li {
  display: inline;
  line-height: 1.2;
  vertical-align: top;
  font-size: var(--small);
}

.breadcrumb li a,
.breadcrumb li a:visited {
  font-weight: normal;
  color: #555;
}

.breadcrumb li:after {
  content: '\203A';
  color: #777;
  margin: 0 5px;
}

.breadcrumb li:last-child a {
  pointer-events: none;
  text-decoration: none;
  color: #999;
}

.breadcrumb li:last-child:after {
  content: '';
  margin: 0;
}

.flatpagedata :deep(h1:first-of-type) {
  margin: 0;
}

.flatpagedata :deep(img) {
  max-width: 100%;
}

.flatpagedata :deep(.caption p) {
  text-align: center !important;
  font-style: italic;
  font-weight: normal;
  font-size: smaller;
}

.flatpagedata :deep(h1),
.flatpagedata :deep(h2),
.flatpagedata :deep(h3),
.flatpagedata :deep(h4),
.flatpagedata :deep(h5) {
  color: var(--colour-dark-blue);
  line-height: 1.2;
  text-align: left !important;
}

.flatpagedata :deep(h2),
.flatpagedata :deep(h3),
.flatpagedata :deep(h4) {
  margin: 1.25em 0 0.5em;
}

.flatpagedata :deep(h1 + h2) {
  margin-top: 0;
}

.flatpagedata {
  font-size: 16px;
  line-height: 1.5;
}

.flatpagedata :deep(li),
.flatpagedata :deep(p) {
  text-align: left !important;
  font-size: 16px;
  line-height: 1.5;
}

.flatpagedata :deep(li p) {
  font-size: inherit;
}

.flatpagedata :deep(em) {
  font-style: italic;
}

.flatpagedata :deep(ul ul) {
  padding-left: 0;
}

.flatpagedata :deep(ul ul li) {
  list-style-type: circle;
}

.flatpagedata :deep(ul) {
  padding-left: 0;
  margin-bottom: 0.8em;
}

.flatpagedata :deep(li) {
  list-style: disc;
  margin-bottom: 0.5em;
  margin-left: 1em;
}

.flatpagedata :deep(a:not(.button)) {
  font-weight: 600;
  text-decoration: underline;
  transition: background-color 0.2s ease-out;
  color: var(--colour-dark-green);
}

.flatpagedata :deep(a:not(.button):active) {
  background-color: var(--colour-page-background);
  border-radius: 3px;
}

.flatpagedata :deep(.table-wrapper) {
  overflow: auto;
  table-layout: fixed;
  width: 100%;
  max-width: calc(100vw - 2em);
}

.flatpagedata :deep(table tr td:first-of-type) {
  position: sticky;
  left: 0;
  z-index: 1;
  background: white;
}

.flatpagedata :deep(table) {
  border-color: #ccc;
  border-collapse: collapse;
  border-spacing: 0;
}

.flatpagedata :deep(table td),
.flatpagedata :deep(table p),
.flatpagedata :deep(table li) {
  font-size: 12px;
  line-height: 1.2;
}

.flatpagedata :deep(table tr td:first-of-type) {
  font-weight: bold;
  font-size: var(--body);
}

.flatpagedata :deep(table td) {
  vertical-align: middle;
  padding: 5px;
  line-height: 1.2;
}

.flatpagedata :deep(table tr:nth-child(even) td) {
  background-color: #f5f5f5;
}

.flatpagedata :deep(.cta-btn),
.flatpagedata :deep(.button.info) {
  appearance: none;
  background: var(--colour-light-green);
  border: none;
  box-shadow: 0 0.375em var(--colour-dark-green);
  color: #fff;
  cursor: pointer;
  display: inline-block;
  font-size: 1em;
  font-weight: bold;
  padding: 0.8em 1em 0.7em;
  text-align: center;
  text-decoration: none;
  user-select: none;
}

.flatpagedata :deep(.cta-btn:active),
.flatpagedata :deep(.button.info:active) {
  background: var(--colour-dark-green);
  box-shadow: 0 0.175em #154920;
}

.published-date {
  font-size: var(--body);
  font-weight: normal;
  color: var(--colour-text-light);
  margin: 0.5em 0 1.25em;
}

/* promo page widget */
.flatpagedata :deep(.promo-page-links) {
  border-top: 1px solid var(--colour-page-background);
  column-gap: 1.5em;
  display: grid;
  gap: 0.75em;
  grid-template-columns: repeat(auto-fit, minmax(max(16ch, 30ch), 1fr));
  margin: 1.5em 0;
  padding-top: 0.75em;
}

.flatpagedata :deep(.item) {
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
  display: flex;
  overflow: hidden;
  text-decoration: none !important;
  border-bottom: 1px solid var(--colour-page-background);
  padding: 0 0 0.75em 0;
}

.flatpagedata :deep(.item .title) {
  color: var(--colour-dark-blue);
  font-size: 1em;
  font-weight: bold;
  line-height: 1.2;
  margin: auto 0;
  padding: 0 1em;
}

.flatpagedata :deep(.item img) {
  border-radius: 0.4em;
  flex-shrink: 0;
  margin: 0 !important;
  max-width: none;
  width: 7em;
}

@media (max-width: 768px) {
  .flatpagedata :deep(img) {
    float: none !important;
    height: auto;
    margin: 1em -16px 1.4em !important;
    max-width: 100vw;
    width: 100vw;
  }

  .flatpagedata :deep(.caption) {
    float: none !important;
    margin: 0 -16px 0 !important;
  }
}

/* desktop & tablet */
article.desktop {
  margin: auto;
  max-width: 50em;
  padding: 0 var(--page-width-padding) 1.4em;
  width: 100%;
}

.desktop .flatpagedata :deep(img) {
  max-width: 100%;
  width: auto;
  border-radius: 0.5em;
}

.desktop .flatpagedata :deep(img[width='1280']) {
  margin: 0 !important;
}
</style>
